import Vue from 'vue'
import VueRouter from 'vue-router'

import store from '../store/index.js'
import { createOwnProfileGuard } from './ownProfileGuard.js'
import { createProfileGuard } from './profileGuard.js'
import { scrollBehavior } from './scrollBehavior.js'

// Nav pages
const Home      = () => import('../views/Home.vue')
const Docs      = () => import('../views/Docs.vue')
const Shop      = () => import('../views/Shop.vue')
const Leaderboard = () => import('../views/Leaderboard.vue')
const Premium   = () => import('../views/Premium.vue')
const Profile = () => import('../views/Profile.vue')
// The three panels behind /profile/:id. Separate chunks, so a visitor who
// never opens Stats never downloads the bubble physics behind it.
const ProfileOverview = () => import('../components/Profile/ProfileOverview.vue')
const ProfileStatsPanel = () => import('../components/Profile/ProfileStatsPanel.vue')
const ProfileCustomizePanel = () => import('../components/Profile/ProfileCustomizePanel.vue')
const Blog      = () => import('../views/Blog.vue')
const BlogPost  = () => import('../views/BlogPost.vue')

// Other pages
const Privacy  = () => import('../views/Privacy.vue')
const Terms    = () =>  import('../views/Terms.vue')

// WebUI pages
const WebUI = () =>  import('../views/WebUI.vue')

// Redirect pages
//
// `/invite` and `/discord` are deliberately absent: the Express app owns
// them (server/routes/pages.js redirects both off-site). A URL must have
// exactly one owner — when both sides claim one, it behaves differently
// depending on whether you typed it or clicked it, which is how `/docs`
// ended up 404ing on direct navigation. Link to them with a plain `<a
// href>`, not `<router-link>`.
const Authenticate  = () =>  import('../views/Authenticate.vue')
const Login         = () =>  import('../views/Login.vue')
const PageNotFound  = () =>  import('../views/404.vue')
const Success       = () =>  import('../views/Success.vue')

Vue.use(VueRouter)

export const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
  },
  {
    path: '/docs',
    name: 'Docs',
    component: Docs
  },
  {
    path: '/commands',
    redirect: '/docs'
  },
  {
    path: '/shop',
    name: 'Shop',
    component: Shop
  },
  {
    path: '/leaderboard',
    name: 'Leaderboard',
    component: Leaderboard
  },
  {
    path: '/premium',
    name: 'Premium',
    component: Premium,
    meta: {
      // The hero is flat black, so the gradient canvas would render 700px of
      // animated WebGL behind an opaque page and never be seen. Opting out
      // also pins the navbar: App.vue derives that from this flag, since a
      // transparent navbar only works when the gradient is behind it.
      hideGradient: true,
    }
  },
  {
    path: '/blog',
    name: 'Blog',
    component: Blog
  },
  {
    path: '/post/:slug',
    name: 'Post',
    component: BlogPost,
    meta: {
      hideGradient: true,
    }
  },
  {
    path: '/profile/:id',
    component: Profile,
    // No `name` on the parent: naming a route that only ever renders a
    // child makes `router.push({ name: 'Profile' })` land on a shell with an
    // empty <router-view>. The children carry the names.
    //
    // Signed-in only, and `/profile/me` never renders — both decided by a
    // global guard below, which sends a signed-out visitor to /login and
    // resolves `me` to a real id. Deliberately *not* `beforeEnter`: see the
    // comment on that registration.
    children: [
      {
        path: '',
        name: 'ProfileOverview',
        component: ProfileOverview,
      },
      {
        path: 'stats',
        name: 'ProfileStats',
        component: ProfileStatsPanel,
      },
      {
        // Your own profile only — see createOwnProfileGuard().
        path: 'customize',
        name: 'ProfileCustomize',
        component: ProfileCustomizePanel,
        beforeEnter: createOwnProfileGuard(() => store.getters.getUserId),
      },
    ],
    meta: {
      // The profile paints its own themed background, so the shared gradient
      // is both invisible behind it and competing with it — and the reward
      // track, the bubble physics and the tier sheens all want the frame
      // budget the WebGL canvas is spending. Pinning the navbar comes with
      // it, which is right: there's no hero here for a transparent one to
      // sit on.
      hideGradient: true,
    }
  },
  {
    path: '/authenticate',
    name: 'Authenticate',
    component: Authenticate,
    meta: {
      blank: true
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: {
      blank: true
    }
  },
  {
    path: '/terms',
    name: 'Terms',
    component: Terms,
    
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: Privacy
  },
  {
    path: '/success',
    name: 'success',
    component: Success,
    meta: {
      blank: true
    }
  },
  {
    path: '*',
    name: '404',
    component: PageNotFound,
    meta: {
      hideGradient: true
    }
  },
  {
    path: '/ui/:id',
    name: 'WebUI',
    component: WebUI,
    meta: {
      blank: true
    }
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.VUE_APP_BASE_URL,
  routes,
  // See scrollBehavior.js — extracted so it can be tested without building a
  // router and importing every view.
  scrollBehavior,
})

/*
 * Profiles are signed-in only, and `/profile/me` is a broker rather than a
 * destination.
 *
 * Global rather than a `beforeEnter` on the profile route, which is where
 * this started and where it was quietly wrong. `beforeEnter` runs when a
 * route *record* is entered, and `/profile/999` and `/profile/me` are the
 * same record with different params — so following "Go to your profile" from
 * a profile you cannot see changed the params, skipped the guard entirely,
 * and left a logged-out visitor sitting on a blank `/profile/me` instead of
 * being sent to log in.
 *
 * Reproduced before changing anything: pushing `/profile/me` from `/shop`
 * redirected correctly, and pushing it from `/profile/<someone>` did not.
 * That difference is the whole bug.
 *
 * `beforeEach` runs on every navigation, param changes included, so there is
 * now no route you can arrive from that skips it — which matters twice as
 * much now that it also decides whether the page may be seen at all. The
 * guard returns `next()` untouched for anything outside `/profile/`, so this
 * costs every other navigation one regex test.
 */
router.beforeEach(createProfileGuard({
  isLoggedIn: () => Boolean(store.getters.getToken),
  getUserId: () => store.getters.getUserId,
  loadUser: () => store.dispatch('fetchUserInfo'),
}))

export default router
